import * as React from "react";
import * as MUI from "@material-ui/core";
import {
  AppBody,
  AppExportBody,
  DataToolbar,
  Divider,
  BackButton,
  Fullscreen,
  DataHeader,
  H1,
  H2,
  Delimiter,
  Menu,
  MenuTrigger,
  MenuItem,
  DataToolbarPart,
} from "../basf-ui";
import ExportIcon from "../../icons/Export";
import XlxsIcon from "../../icons/office/XLS";
import PptIcon from "../../icons/office/PPT";
import { FavouriteButton } from "../favourites/components";
import { useHookSubscribe } from "../react-rxjs";
import { BASFChartMap, BASFExportChartMap } from "./components/BASFChartMap";
import { DataSourceControl } from "./controls/DataSourceControl";
import { DimensionControl } from "./controls/DimensionControl";
import { useMapStore } from "./hooks/useMapStore";
import { useMapTitle } from "./hooks/useMapTitle";
import { useFavouriteProviderContext } from "./providers/FavouriteProvider";
import { useMapSource } from "./hooks/useMapSource";
import { useMapExport } from "./hooks/useMapExport";
import { DataType } from "@sa/rep-common";
import { MapDataSourceId } from "./models/MapDataSource";

interface MapRootState {
  map?: L.Map;
  setterEU28?: (isEU28: boolean) => void;
}

export function MapRoot() {
  const [state, setState] = React.useState<MapRootState>({});
  const store = useMapStore();
  React.useEffect(() => {
    return store.refresh;
  }, [store]);

  const title = useMapTitle();
  const source = useMapSource();
  const handlerExport = useMapExport(state.map);
  const {
    state: { isFavouriteLoading, isFavouriteDisabled, savedFavouriteItem },
    actions: { addFavourite, deleteFavourite },
  } = useFavouriteProviderContext();

  const loadingData = useHookSubscribe(true, store.loadingData$);
  const loadingDataSources = useHookSubscribe(true, store.loadingDataSources$);
  const loadingDimensions = useHookSubscribe(true, store.loadingDimensions$);
  const loading = loadingData || loadingDataSources || loadingDimensions;

  const handlerExportXLSX = React.useCallback(() => !loading && handlerExport("xlsx"), [handlerExport, loading]);
  const handlerExportPPTX = React.useCallback(() => !loading && handlerExport("pptx"), [handlerExport, loading]);
  const dimensions = useHookSubscribe([], store.dimensions$);
  const refFullscreen = React.useRef<HTMLDivElement | null>(null);

  const handlerMount = React.useCallback((map: L.Map, setterEU28: (isEU28: boolean) => void) => {
    setState({ map, setterEU28 });
  }, []);

  return (
    <AppBody>
      <DataToolbar>
        <BackButton>Back</BackButton>
        <DataToolbarPart>
          <H1>{title}</H1>
          <H2>{source}</H2>
        </DataToolbarPart>
        <Delimiter />
        <FavouriteButton
          isLoading={isFavouriteLoading}
          isDisabled={isFavouriteDisabled}
          handleAdd={addFavourite}
          handleDelete={deleteFavourite}
          favouriteItem={savedFavouriteItem}
        />
        <Menu>
          <MenuTrigger>
            <MUI.IconButton>
              <ExportIcon />
            </MUI.IconButton>
          </MenuTrigger>
          <MenuItem disabled={loading} icon={XlxsIcon} onClick={handlerExportXLSX}>
            XLS
          </MenuItem>
          <MenuItem disabled={loading} icon={PptIcon} onClick={handlerExportPPTX}>
            Download PPT
          </MenuItem>
        </Menu>
        <Fullscreen refContent={refFullscreen} noPadding />
      </DataToolbar>
      <Divider />
      <DataHeader>
        <DataSourceControl />
        {dimensions
          .filter((e) => e.visible)
          .map((e) => (
            <DimensionControl dimension={e} key={e.id} />
          ))}
      </DataHeader>
      <Divider />
      <div ref={refFullscreen} style={{ position: "relative", display: "flex", flex: 1 }}>
        <BASFChartMap onMount={handlerMount} />
      </div>
    </AppBody>
  );
}

export function MapExportRoot() {
  const [state, setState] = React.useState<MapRootState>({});
  const title = useMapTitle();
  const source = useMapSource();
  const store = useMapStore();
  const dimensions = useHookSubscribe([], store.dimensions$);
  window["loadReport"] = async function () {
    await new Promise((resolve) => setTimeout(resolve, 3000));
  };

  window["setParameterValue"] = React.useCallback(
    async (parameterId: string, dataType: DataType, value?: unknown) => {
      if (value !== undefined) {
        switch (parameterId) {
          case "@data_source": {
            store.dataSourceId$.next(Number(value) as MapDataSourceId);
            break;
          }
          case "@zoom": {
            state.map?.setZoom(Number(value));
            break;
          }
          case "@center": {
            state.map?.fitBounds([JSON.parse(value as string)]);
            break;
          }
          case "@eu28": {
            state.setterEU28 && state.setterEU28(String(value).toLowerCase() === "true");
            break;
          }
          default: {
            const primaryHierarchyKey = parameterId.replace("@", "");
            const dimension = dimensions.find((e) => e.hierarchy.includes(primaryHierarchyKey));
            if (dimension?.visible) {
              const value$ = dimension ? store.dimensionsValues.get(dimension.id) : undefined;
              const values = value as unknown[];
              value$?.next(values[0]);
            }
          }
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    },
    [dimensions, state]
  );

  const handlerMount = React.useCallback((map: L.Map, setterEU28: (isEU28: boolean) => void) => {
    setState({ map, setterEU28 });
  }, []);

  return (
    <AppExportBody h1={<H1>{title}</H1>} h2={<H2>{source}</H2>}>
      <BASFExportChartMap options={{ zoomControl: false }} onMount={handlerMount} />
    </AppExportBody>
  );
}
